import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ShopMap.css';

/**
 * Leaflet + OpenStreetMap shop picker. Free, no API key.
 * With `withSearch`, renders a search box + result list beside the map:
 * typing filters both the list and the pins; clicking either selects.
 */
function ShopMap({ shops = [], selectedId = null, onSelect = null, height = 380, withSearch = false }) {
    const divRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const [query, setQuery] = useState('');

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return shops;
        return shops.filter(s =>
            [s.name, s.address, s.fee_info, s.description]
                .some(v => v && v.toLowerCase().includes(q))
        );
    }, [shops, query]);

    useEffect(() => {
        if (!divRef.current || mapRef.current) return;
        const map = L.map(divRef.current, { scrollWheelZoom: true })
            .setView([39.5, -98.35], 4); // continental US default
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
        }).addTo(map);
        mapRef.current = map;
        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        markersRef.current.forEach(m => m.remove());
        const located = filtered.filter(s => s.lat != null && s.lng != null);

        markersRef.current = located.map(s => {
            const isSelected = String(s.id) === String(selectedId);
            const icon = L.divIcon({
                className: `shop-pin${isSelected ? ' selected' : ''}`,
                html: '◆',
                iconSize: [26, 26],
                iconAnchor: [13, 13],
            });
            const marker = L.marker([+s.lat, +s.lng], { icon }).addTo(map);
            const rating = s.rating_avg
                ? `★ ${s.rating_avg} (${s.rating_count})`
                : 'No ratings yet';
            // Built from DOM nodes, not an HTML string: the name and fee
            // line are typed by the shop owner and must display literally
            const pop = document.createElement('div');
            const strong = document.createElement('strong');
            strong.textContent = s.name;
            pop.append(strong, document.createElement('br'));
            if (s.fee_info) pop.append(document.createTextNode(s.fee_info), document.createElement('br'));
            const rate = document.createElement('span');
            rate.className = 'shop-pop-rating';
            rate.textContent = rating;
            pop.append(rate, document.createElement('br'));
            const link = document.createElement('a');
            link.href = `/store/${s.id}`;
            link.textContent = 'Store page →';
            pop.append(link);
            marker.bindPopup(pop);
            if (onSelect) marker.on('click', () => onSelect(s));
            return marker;
        });

        if (located.length === 1) {
            map.setView([+located[0].lat, +located[0].lng], 13);
        } else if (located.length > 1) {
            const group = L.featureGroup(markersRef.current);
            map.fitBounds(group.getBounds().pad(0.25));
        }
    }, [filtered, selectedId, onSelect]);

    // Fly to the selected shop when it changes
    useEffect(() => {
        const map = mapRef.current;
        if (!map || selectedId == null) return;
        const s = shops.find(x => String(x.id) === String(selectedId));
        if (s && s.lat != null) map.flyTo([+s.lat, +s.lng], 13, { duration: 0.6 });
    }, [selectedId, shops]);

    const mapEl = <div className="shop-map" ref={divRef} style={{ height }} />;
    if (!withSearch) return mapEl;

    return (
        <div className="shop-picker">
            <div className="shop-picker-side" style={{ maxHeight: height }}>
                <input
                    className="shop-picker-search"
                    placeholder="Search shops by name, city, fees…"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
                <ul className="shop-picker-list">
                    {filtered.length === 0 && (
                        <li className="shop-picker-empty">No shops match.</li>
                    )}
                    {filtered.map(s => (
                        <li key={s.id}>
                            <button
                                className={`shop-picker-item${String(s.id) === String(selectedId) ? ' selected' : ''}`}
                                onClick={() => onSelect && onSelect(s)}
                            >
                                <span className="shop-picker-name">
                                    {s.name}
                                    <span className="shop-picker-rating">
                                        {s.rating_avg ? `★ ${s.rating_avg}` : 'no ratings'}
                                    </span>
                                </span>
                                {s.fee_info && <span className="shop-picker-fee">{s.fee_info}</span>}
                                {s.address && <span className="shop-picker-addr">{s.address}</span>}
                                <span
                                    className="shop-picker-storelink"
                                    role="link"
                                    onClick={e => { e.stopPropagation(); window.open(`/store/${s.id}`, '_blank'); }}
                                >
                                    Store page ↗
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
            {mapEl}
        </div>
    );
}

export default ShopMap;
